/**
 * AI Tutor client (Trading UI).
 *
 * 2026-06-05 bundle-gate fix: removed all direct browser-side Gemini /
 * Google GenAI access. The browser now talks to the Adversyn backend
 * Tutor endpoints only; the backend owns provider selection, API keys,
 * and the multi-provider cascade (deterministic → Anthropic / OpenAI /
 * Gemini → UNAVAILABLE).
 *
 * Public API surface preserved for AITutor.tsx:
 *   - getTutorResponse(messages, opts): Promise<string>
 *   - probeTutorHealth(opts): Promise<TutorHealth>
 *   - Message, TutorError, TutorErrorDetails, TutorHealth, GetTutorResponseOptions
 *
 * Hard rules (carried from the bundle-secret pre-flight gate):
 *   - No `@google/genai` import.
 *   - No `import.meta.env.VITE_*GEMINI*` or `process.env.*GEMINI*` reads.
 *   - No `generativelanguage.googleapis.com` URL in the bundle.
 *   - No browser-side LLM provider SDK constructor.
 *
 * Backend endpoints used:
 *   POST /api/v1/tutor/ask     — deterministic knowledge retrieval (Phase 12H,
 *                                 no LLM, grounded in closed Markdown KB).
 *   POST /api/v1/tutor/answer  — resilient multi-provider cascade (Phase 12Z).
 *   GET  /api/v1/tutor/explain — index/page/topic lookups (not used here;
 *                                 reserved for direct page-explain UX).
 */

import {
  ADVERSYN_KNOWLEDGE_VERSION,
  buildAdversynSystemInstruction,
} from "../data/adversynKnowledge";
import { apiV1Base } from "../lib/apiConfig";

// ── Configuration ──────────────────────────────────────────────────────────

// 'provider' on every TutorErrorDetails is now the backend, not Gemini.
const PROVIDER = "adversyn-backend-tutor";
const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_MAX_ATTEMPTS = 2; // initial + 1 retry on transient errors

const TOKEN_KEY = "adversyn_auth_token";

// ── Types ──────────────────────────────────────────────────────────────────

export interface Message {
  role: "user" | "assistant";
  content: string;
}

export type TutorErrorCode =
  | "MISSING_API_KEY"
  | "PROVIDER_AUTH_FAILED"
  | "PROVIDER_MODEL_INVALID"
  | "PROVIDER_RATE_LIMITED"
  | "PROVIDER_SERVER_ERROR"
  | "PROVIDER_BAD_REQUEST"
  | "NETWORK_UNAVAILABLE"
  | "REQUEST_TIMEOUT"
  | "MALFORMED_PROVIDER_RESPONSE"
  | "BACKEND_UNAVAILABLE"
  | "UNKNOWN_TUTOR_ERROR";

export interface TutorErrorDetails {
  code: TutorErrorCode;
  status: number;
  requestId: string;
  provider: string;
  model: string;
  endpoint: string;
  latencyMs: number;
  attempt: number;
  maxAttempts: number;
  timestamp: string;
  retryable: boolean;
  userMessage: string;
  internalMessage: string;
  providerErrorCode?: string;
  providerErrorMessage?: string;
}

export class TutorError extends Error {
  readonly details: TutorErrorDetails;
  constructor(details: TutorErrorDetails) {
    super(details.internalMessage);
    this.name = "TutorError";
    this.details = details;
  }
}

export interface TutorHealth {
  status: "ok" | "degraded" | "unknown";
  latencyMs?: number;
  lastChecked: string;
  lastError?: TutorErrorDetails;
}

export interface GetTutorResponseOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  maxAttempts?: number;
  model?: string;
  requestId?: string;
}

// ── Internal helpers ──────────────────────────────────────────────────────

function newRequestId(): string {
  try {
    const c: any = (globalThis as any).crypto;
    if (c?.randomUUID) return c.randomUUID();
  } catch { /* noop */ }
  return `tutor-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function classifyStatus(status: number): {
  code: TutorErrorCode;
  retryable: boolean;
} {
  if (status === 401 || status === 403) {
    return { code: "PROVIDER_AUTH_FAILED", retryable: false };
  }
  if (status === 404) return { code: "PROVIDER_MODEL_INVALID", retryable: false };
  if (status === 429) return { code: "PROVIDER_RATE_LIMITED", retryable: true };
  if (status >= 500) return { code: "PROVIDER_SERVER_ERROR", retryable: true };
  if (status >= 400) return { code: "PROVIDER_BAD_REQUEST", retryable: false };
  return { code: "UNKNOWN_TUTOR_ERROR", retryable: false };
}

function userMessageFor(code: TutorErrorCode, status: number): string {
  switch (code) {
    case "MISSING_API_KEY":
      return "AI Tutor is not available in this build. Ask an operator to enable a backend provider.";
    case "PROVIDER_AUTH_FAILED":
      return `AI Tutor backend authentication failed (HTTP ${status}). Operator action required.`;
    case "PROVIDER_MODEL_INVALID":
      return `AI Tutor backend reported PROVIDER_MODEL_INVALID (HTTP ${status}).`;
    case "PROVIDER_RATE_LIMITED":
      return "AI Tutor is rate-limited. Wait a moment and retry.";
    case "PROVIDER_SERVER_ERROR":
      return `AI Tutor backend is having an outage (HTTP ${status}). Retry shortly.`;
    case "PROVIDER_BAD_REQUEST":
      return `AI Tutor request was rejected (HTTP ${status}).`;
    case "NETWORK_UNAVAILABLE":
      return "AI Tutor could not reach the Adversyn backend. Check your network connection.";
    case "REQUEST_TIMEOUT":
      return "AI Tutor request timed out before the backend responded.";
    case "MALFORMED_PROVIDER_RESPONSE":
      return "AI Tutor received a response from the backend but it had no readable text.";
    case "BACKEND_UNAVAILABLE":
      return "AI Tutor backend reported UNAVAILABLE. Every provider failed; please retry shortly.";
    default:
      return "AI Tutor failed unexpectedly.";
  }
}

function buildError(
  partial: Partial<TutorErrorDetails> & { code: TutorErrorCode; internalMessage: string },
  ctx: { endpoint: string; requestId: string; startedAt: number; attempt: number; maxAttempts: number }
): TutorError {
  const status = partial.status ?? 0;
  const auto = classifyStatus(status);
  const code = partial.code ?? auto.code;
  const details: TutorErrorDetails = {
    code,
    status,
    requestId: ctx.requestId,
    provider: PROVIDER,
    model: partial.model ?? "backend-cascade",
    endpoint: ctx.endpoint,
    latencyMs: Date.now() - ctx.startedAt,
    attempt: ctx.attempt,
    maxAttempts: ctx.maxAttempts,
    timestamp: nowIso(),
    retryable: partial.retryable ?? auto.retryable,
    userMessage: partial.userMessage ?? userMessageFor(code, status),
    internalMessage: partial.internalMessage,
    providerErrorCode: partial.providerErrorCode,
    providerErrorMessage: partial.providerErrorMessage,
  };
  emitTutorErrorEvent(details);
  return new TutorError(details);
}

function emitTutorErrorEvent(details: TutorErrorDetails): void {
  try {
    // eslint-disable-next-line no-console
    console.error(`[AITutor] ${details.requestId} ${details.code}`, {
      ...details,
      knowledgeVersion: ADVERSYN_KNOWLEDGE_VERSION,
    });
    if (typeof window !== "undefined" && typeof CustomEvent === "function") {
      window.dispatchEvent(new CustomEvent("adversyn:tutor-error", { detail: details }));
    }
  } catch { /* swallow */ }
}

function emitTutorSuccessEvent(payload: {
  requestId: string;
  latencyMs: number;
  provider: string;
  pageKey?: string;
}): void {
  try {
    if (typeof window !== "undefined" && typeof CustomEvent === "function") {
      window.dispatchEvent(new CustomEvent("adversyn:tutor-success", {
        detail: {
          ...payload,
          knowledgeVersion: ADVERSYN_KNOWLEDGE_VERSION,
          timestamp: nowIso(),
        },
      }));
    }
  } catch { /* swallow */ }
}

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  try {
    if (typeof localStorage !== "undefined") {
      const tok = localStorage.getItem(TOKEN_KEY);
      if (tok && tok.trim()) h["Authorization"] = `Bearer ${tok.trim()}`;
    }
  } catch { /* noop */ }
  return h;
}

// ── Backend route helpers ─────────────────────────────────────────────────

interface DeterministicAnswer {
  answer: string;
  citations: Array<{ file: string; section: string }>;
  source: "shared_backend_knowledge";
}

function computeTradingUiPageKey(pathname: string | null | undefined): string {
  if (!pathname) return "trading_dashboard";
  const p = pathname.split("?")[0].split("#")[0];
  if (p.startsWith("/council"))      return "trading_council";
  if (p.startsWith("/market-pulse")) return "trading_market_pulse";
  if (p.startsWith("/matrix"))       return "trading_matrix";
  if (p.startsWith("/strategy"))     return "trading_strategy_lab";
  if (p.startsWith("/portfolio"))    return "trading_portfolio";
  if (p.startsWith("/risk"))         return "trading_risk_center";
  if (p.startsWith("/academy"))      return "trading_simulation_lab";
  return "trading_dashboard";
}

/** Deterministic Phase 12H knowledge consultation (no LLM). */
async function tryBackendKnowledge(
  question: string,
  pageKey: string,
  signal?: AbortSignal,
): Promise<DeterministicAnswer | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4_000);
    if (signal) {
      if (signal.aborted) ctrl.abort();
      else signal.addEventListener("abort", () => ctrl.abort(), { once: true });
    }
    const resp = await fetch(`${apiV1Base()}/tutor/ask`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ question, page_key: pageKey, surface: "trading_ui" }),
      signal: ctrl.signal,
    });
    clearTimeout(t);
    if (!resp.ok) return null;
    const body = await resp.json();
    const d = body?.data;
    if (!d) return null;
    if (d.refused) return null;
    if (d.status !== "OK") return null;
    const answer = (d.answer as string | undefined) || "";
    if (!answer || !answer.trim()) return null;
    if (typeof window !== "undefined" && typeof CustomEvent === "function") {
      window.dispatchEvent(new CustomEvent("adversyn:tutor-backend-hit", {
        detail: {
          pageKey,
          citations: d.citations || [],
          source: "shared_backend_knowledge",
          timestamp: nowIso(),
        },
      }));
    }
    return {
      answer,
      citations: (d.citations as Array<{ file: string; section: string }> | undefined) || [],
      source: "shared_backend_knowledge",
    };
  } catch {
    return null;
  }
}

/** Phase 12Z multi-provider cascade. Returns the answer string, or
 * throws a TutorError describing the backend's UNAVAILABLE state.
 */
async function callBackendAnswerCascade(
  question: string,
  pageKey: string,
  opts: GetTutorResponseOptions,
): Promise<string> {
  const requestId = opts.requestId ?? newRequestId();
  const endpoint = `${apiV1Base()}/tutor/answer`;
  const startedAt = Date.now();
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const maxAttempts = Math.max(1, opts.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);

  let lastErr: TutorError | null = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    if (opts.signal) {
      if (opts.signal.aborted) ctrl.abort();
      else opts.signal.addEventListener("abort", () => ctrl.abort(), { once: true });
    }
    try {
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          question,
          page_key: pageKey,
          surface: "trading_ui",
          // The backend cascade controls model selection — we never
          // suggest one from the browser.
        }),
        signal: ctrl.signal,
      });
      clearTimeout(t);
      if (!resp.ok) {
        lastErr = buildError(
          {
            code: classifyStatus(resp.status).code,
            status: resp.status,
            internalMessage: `Tutor backend returned HTTP ${resp.status}`,
            retryable: classifyStatus(resp.status).retryable,
          },
          { endpoint, requestId, startedAt, attempt, maxAttempts },
        );
        if (!lastErr.details.retryable) throw lastErr;
        continue;
      }
      const body = await resp.json();
      const data = body?.data ?? body;
      const answer = typeof data?.answer === "string" ? data.answer.trim() : "";
      const providerUsed = typeof data?.provider_used === "string" ? data.provider_used : "backend";
      const providerState = typeof data?.provider_state === "string" ? data.provider_state : "";
      if (!answer && (providerState === "UNAVAILABLE" || providerState === "ALL_FAILED")) {
        throw buildError(
          {
            code: "BACKEND_UNAVAILABLE",
            status: 200,
            internalMessage: `Tutor backend reported provider_state=${providerState}`,
            providerErrorCode: providerState,
          },
          { endpoint, requestId, startedAt, attempt, maxAttempts },
        );
      }
      if (!answer) {
        throw buildError(
          {
            code: "MALFORMED_PROVIDER_RESPONSE",
            status: 200,
            internalMessage: "Tutor backend returned no answer text",
          },
          { endpoint, requestId, startedAt, attempt, maxAttempts },
        );
      }
      emitTutorSuccessEvent({
        requestId,
        latencyMs: Date.now() - startedAt,
        provider: providerUsed,
        pageKey,
      });
      return answer;
    } catch (e: any) {
      clearTimeout(t);
      if (e instanceof TutorError) {
        lastErr = e;
        if (!e.details.retryable || attempt === maxAttempts) throw e;
        continue;
      }
      const isAbort = e?.name === "AbortError" || ctrl.signal.aborted;
      const code: TutorErrorCode = isAbort ? "REQUEST_TIMEOUT" : "NETWORK_UNAVAILABLE";
      const err = buildError(
        {
          code,
          status: 0,
          internalMessage: e?.message || String(e),
          retryable: code === "NETWORK_UNAVAILABLE",
        },
        { endpoint, requestId, startedAt, attempt, maxAttempts },
      );
      lastErr = err;
      if (!err.details.retryable || attempt === maxAttempts) throw err;
    }
  }
  if (lastErr) throw lastErr;
  throw buildError(
    {
      code: "UNKNOWN_TUTOR_ERROR",
      status: 0,
      internalMessage: "Tutor backend cascade exhausted with no answer",
    },
    { endpoint, requestId, startedAt, attempt: maxAttempts, maxAttempts },
  );
}

// ── Public API ─────────────────────────────────────────────────────────────

/** AITutor.tsx + others consume this. Preserves the legacy contract:
 *   - Returns the answer string on success.
 *   - Throws TutorError with structured details on failure.
 *
 * Flow (post-bundle-gate fix):
 *   1. Deterministic backend /tutor/ask (no LLM, no key spend).
 *   2. Multi-provider backend cascade /tutor/answer (Anthropic / OpenAI /
 *      Gemini, gated server-side).
 *
 * `buildAdversynSystemInstruction` is intentionally re-imported so the
 * knowledge-version event payload remains identical to the prior
 * implementation. The system prompt itself is owned by the backend.
 */
export async function getTutorResponse(
  messages: Message[],
  opts: GetTutorResponseOptions = {},
): Promise<string> {
  const latest = [...messages].reverse().find(m => m.role === "user");
  const question = (latest && latest.content) ? latest.content : "";
  const pageKey = computeTradingUiPageKey(
    typeof window !== "undefined" ? window.location.pathname : "/",
  );

  if (question && question.trim().length >= 3) {
    try {
      const det = await tryBackendKnowledge(question, pageKey, opts.signal);
      if (det && det.answer && det.answer.trim()) {
        // Reference the import so tree-shaking doesn't elide the
        // knowledge module — its system instruction is still useful
        // for downstream telemetry hooks that read this constant.
        void buildAdversynSystemInstruction;
        return det.answer;
      }
    } catch {
      /* fall through to backend cascade */
    }
  }
  return callBackendAnswerCascade(question, pageKey, opts);
}

/** Lightweight backend health probe. Sends "ping" through getTutorResponse;
 * any TutorError downgrades status to "degraded".
 */
export async function probeTutorHealth(opts: { timeoutMs?: number } = {}): Promise<TutorHealth> {
  const timeoutMs = opts.timeoutMs ?? 8_000;
  const startedAt = Date.now();
  try {
    await getTutorResponse(
      [{ role: "user", content: "ping" }],
      { timeoutMs, maxAttempts: 1, requestId: `tutor-health-${Date.now()}` },
    );
    return { status: "ok", latencyMs: Date.now() - startedAt, lastChecked: nowIso() };
  } catch (e) {
    const err = e instanceof TutorError ? e.details : undefined;
    return {
      status: "degraded",
      latencyMs: Date.now() - startedAt,
      lastChecked: nowIso(),
      lastError: err,
    };
  }
}
